import { hasItems } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";
import {
  type CreateTeamPayload,
  type Team,
  type TeamSearch,
  type UpdateTeamPayload,
} from "./types";

export const useMyTeams = () => {
  return useSWR<Team[]>("/coworkers/teams", (url) => fetcher.core.get(url), {
    revalidateOnFocus: false,
  });
};

export const useTeam = (
  teamId?: number | null,
  revalidateOnFocus?: boolean
) => {
  return useSWR<Team>(
    teamId ? `/team-detail/${teamId}` : null,
    () => fetcher.core.get(`/teams/${teamId}`),
    {
      revalidateOnFocus: revalidateOnFocus ?? true,
    }
  );
};

export const loadTeam = async (teamId: number) =>
  fetcher.core.get<Team>(`/teams/${teamId}`);

export const createTeam = async (newTeam: CreateTeamPayload) => {
  const res = await fetcher.core.post<Team, false>("/teams", newTeam);
  mutate(`/coworkers/teams`);
  return res;
};

export const updateTeam = async (
  teamId: number,
  updatedTeam: UpdateTeamPayload
) => {
  const res = await fetcher.core.update(`/teams/${teamId}`, updatedTeam);
  mutate(`/coworkers/teams`);
  mutate(`/team-detail/${teamId}`);
  return res;
};

export const deleteTeam = async (teamId: number) => {
  const res = await fetcher.core.remove(`/teams/${teamId}`);
  mutate(`/coworkers/teams`);
  return res;
};

export const useSearchTeam = (search?: TeamSearch) => {
  return useSWR<Team[]>(
    !isEmpty(search?.searchTerm) ||
      hasItems(search?.buCodes) ||
      hasItems(search?.hfbs)
      ? `/teams/search/${[
          search?.searchTerm,
          ...(search?.buCodes ?? []),
          ...(search?.hfbs ?? []),
        ].join(",")}`
      : null,
    () => {
      return searchTeam(search);
    }
  );
};

const searchTeam = async (search?: TeamSearch) => {
  if (
    isEmpty(search?.searchTerm) &&
    !hasItems(search?.buCodes) &&
    !hasItems(search?.hfbs)
  ) {
    return [];
  }
  const res = await fetcher.core.post<Team[], false>("/teams/search", {
    users: search?.searchTerm?.includes("@") ? [search.searchTerm] : undefined,

    title: !search?.searchTerm?.includes("@") ? search?.searchTerm : undefined,
    hfbs: hasItems(search?.hfbs) ? search?.hfbs : undefined,
    buCodes: hasItems(search?.buCodes) ? search?.buCodes : undefined,
  });

  return res ?? [];
};
